import type { OrganizationModel } from '~/components/types/organization-types';
import { isFeatureEnabled } from '~/Utils';

/**
 * Type representing a permission section with optional feature flag requirement
 */
interface PermissionSection {
  /** Optional feature flag name that must be enabled for this section to be visible */
  requiredFeatureFlag?: string;
  [key: string]: unknown;
}

/**
 * Type representing a permission item with optional payments configuration and feature flag requirements
 */
interface PermissionItem {
  /** Optional payments configuration property name that must be truthy for this item to be visible */
  requiredPaymentsConfig?: string;
  /** Optional feature flag name that must be enabled for this item to be visible */
  requiredFeatureFlag?: string;
  [key: string]: unknown;
}

/**
 * Type for payments configuration object
 */
interface PaymentsConfiguration {
  [key: string]: unknown;
}

/**
 * Checks if a section should be visible based on its feature flag requirement
 * @param section - The section header object
 * @param organization - The organization to check feature flags against
 * @returns True if section should be visible, false otherwise
 */
export const isSectionVisible = (section: PermissionSection, organization: OrganizationModel): boolean => {
  // If no feature flag is required, section is always visible
  if (!section.requiredFeatureFlag) {
    return true;
  }

  // Check if the required feature flag is enabled for this organization
  return isFeatureEnabled(organization, section.requiredFeatureFlag);
};

/**
 * Filters an array of sections to only include visible ones based on feature flags
 * @param sections - Array of section header objects
 * @param organization - The organization to check feature flags against
 * @returns Filtered array of visible sections
 */
export const getVisibleSections = (
  sections: PermissionSection[],
  organization: OrganizationModel
): PermissionSection[] => {
  return sections.filter((section) => isSectionVisible(section, organization));
};

/**
 * Checks if a permission item should be visible based on its feature flag and payments configuration requirements
 * @param item - The permission item object
 * @param paymentsConfiguration - The organization's payments configuration
 * @param organization - The organization to check feature flags against
 * @returns True if item should be visible, false otherwise
 */
export const isPermissionItemVisible = (
  item: PermissionItem,
  paymentsConfiguration?: PaymentsConfiguration | null,
  organization?: OrganizationModel | null
): boolean => {
  // Check feature flag requirement
  if (item.requiredFeatureFlag) {
    try {
      if (!organization || !isFeatureEnabled(organization, item.requiredFeatureFlag)) {
        return false;
      }
    } catch {
      return false;
    }
  }

  // If no payments config requirement, item is visible
  if (!item.requiredPaymentsConfig) {
    return true;
  }

  // If no payments configuration provided, hide items that require it
  if (!paymentsConfiguration) {
    return false;
  }

  // Check if the required payments config property is truthy
  return Boolean(paymentsConfiguration[item.requiredPaymentsConfig]);
};
